import Stepper, { Step } from "./Stepper";
import GoldenSeriesAnalysis from "./GoldenSeriesAnalysis";
import UspAnalysis from "./UspAnalysis";
import LinkArrayInput from "./LinkArrayInput";
import SnsLinksInput from "./SnsLinksInput";
import PortfolioSection from "./PortfolioSection";

// hooks
import useLocalStorage from "../../hooks/useLocalStorage";
import useFormData from "../../hooks/useFormData";

import { businessSheetSchema } from "../../utils/formUtils";
import { normalizeSheetItems } from "../../utils/businessSheetUtils";
import { StructuredItemsInput } from "./Cards";
import { TextArea, Fonts, Themes, UploadImage } from "../global/FormElements";

const BusinessSheetCreationForm = () => {
  const { getValue } = useLocalStorage();

  const { formData, errors, handleChangeWithStore, resetForm, resetField, submitForm } =
    useFormData(
      {
        shortBiography: getValue("shortBiography") || "",
        businessDescription: getValue("businessDescription") || "",
        personalInformation: getValue("personalInformation") || "",
        goals: getValue("goals") || "",
        accomplishments: getValue("accomplishments") || "",
        interests: getValue("interests") || "",
        networks: getValue("networks") || "",
        skills: getValue("skills") || "",
        // Issue #91: 構造化アイテム（旧形式のキャッシュも normalize で吸収する）
        goldenEgg: normalizeSheetItems(getValue("goldenEgg"), "goldenSeries"),
        goldenGoose: normalizeSheetItems(getValue("goldenGoose"), "goldenSeries"),
        goldenFarmer: normalizeSheetItems(
          getValue("goldenFarmer"),
          "goldenSeries",
        ),
        companyStrengths: getValue("companyStrengths") || "",
        powerWords: normalizeSheetItems(getValue("powerWords"), "powerWord"),
        itemsProducts: normalizeSheetItems(
          getValue("itemsProducts"),
          "itemProduct",
        ),
        links: getValue("links") || [],
        snsLinks: getValue("snsLinks") || [],
        achievements: getValue("achievements") || [],
        services: getValue("services") || [],
        headerBackgroundImage: null,
        profileImage: null,
        referralSheetBackgroundImage: null,
        colorPreference: getValue("colorPreference") || "",
        fontPreference: getValue("fontPreference") || "",
      },
      businessSheetSchema,
    );

  return (
    <Stepper data={formData} submitForm={submitForm} handleInit={resetForm}>
      {/* step 1 */}
      <Step title="メンバー略歴シート">
        <TextArea
          placeholder="メンバー略歴を入力"
          maxLength={400}
          value={formData?.shortBiography}
          onChange={handleChangeWithStore}
          name="shortBiography"
        />
      </Step>

      {/* step 2 */}
      <Step title="ビジネスについて">
        <TextArea
          placeholder="ビジネスについて入力"
          maxLength={400}
          value={formData?.businessDescription}
          onChange={handleChangeWithStore}
          name="businessDescription"
        />
      </Step>

      {/* step 3 */}
      <Step title="個人的な情報">
        <TextArea
          placeholder="個人的な情報について入力"
          maxLength={1000}
          value={formData?.personalInformation}
          onChange={handleChangeWithStore}
          name="personalInformation"
        />
      </Step>

      {/* step 4 */}
      <Step title="目標">
        <TextArea
          placeholder="目標について入力"
          maxLength={1000}
          value={formData?.goals}
          onChange={handleChangeWithStore}
          name="goals"
        />
      </Step>

      {/* step 5 */}
      <Step title="実績">
        <TextArea
          placeholder="実績について入力"
          maxLength={1000}
          value={formData?.accomplishments}
          onChange={handleChangeWithStore}
          name="accomplishments"
        />
      </Step>

      {/* step 6 */}
      <Step title="興味・関心">
        <TextArea
          placeholder="興味・関心について入力"
          maxLength={1000}
          value={formData?.interests}
          onChange={handleChangeWithStore}
          name="interests"
        />
      </Step>

      {/* step 7 */}
      <Step title="人脈">
        <TextArea
          placeholder="人脈について入力"
          maxLength={1000}
          value={formData?.networks}
          onChange={handleChangeWithStore}
          name="networks"
        />
      </Step>

      {/* step 8 */}
      <Step title="能力">
        <TextArea
          placeholder="能力について入力"
          maxLength={1000}
          value={formData?.skills}
          onChange={handleChangeWithStore}
          name="skills"
        />
      </Step>

      {/* step 9 */}
      <Step title="金のシリーズ分析">
        <GoldenSeriesAnalysis
          onConfirm={({ goldenEgg, goldenGoose, goldenFarmer }) => {
            handleChangeWithStore({ target: { name: "goldenEgg", value: goldenEgg } });
            handleChangeWithStore({ target: { name: "goldenGoose", value: goldenGoose } });
            handleChangeWithStore({ target: { name: "goldenFarmer", value: goldenFarmer } });
          }}
          initialValues={{
            goldenEgg: formData?.goldenEgg,
            goldenGoose: formData?.goldenGoose,
            goldenFarmer: formData?.goldenFarmer,
          }}
        />

        {/* Issue #91: AI が埋めるのは悩みタイトルのみ。内容・アプローチは手入力する */}
        {[
          { name: "goldenEgg", title: "金のタマゴ" },
          { name: "goldenGoose", title: "金のガチョウ" },
          { name: "goldenFarmer", title: "金のファーマー" },
        ].map(({ name, title }) => (
          <div className="goldenSeriesManualInput" key={name}>
            <h4>{title}</h4>
            <StructuredItemsInput
              name={name}
              kind="goldenSeries"
              items={formData?.[name]}
              onChange={(value) =>
                handleChangeWithStore({ target: { name, value } })
              }
            />
          </div>
        ))}
      </Step>

      {/* step 10 */}
      <Step title="USP・パワーワード・商品分析">
        <UspAnalysis
          initialValues={{
            powerWords: formData?.powerWords,
            itemsProducts: formData?.itemsProducts,
          }}
          onConfirm={({ usp, powerWords, itemsProducts }) => {
            handleChangeWithStore({ target: { name: "companyStrengths", value: usp[0] || "" } });
            handleChangeWithStore({ target: { name: "powerWords", value: powerWords } });
            handleChangeWithStore({ target: { name: "itemsProducts", value: itemsProducts } });
          }}
        />
      </Step>

      {/* step 11 */}
      <Step title="自社の強み">
        <TextArea
          placeholder="自社の強みについて入力"
          maxLength={1000}
          value={formData?.companyStrengths}
          onChange={handleChangeWithStore}
          name="companyStrengths"
        />
      </Step>

      {/* step 12 */}
      <Step title="パワーワード">
        {/* Issue #91: タイトル20字 / 内容40字 の2分割 × 6件 */}
        <StructuredItemsInput
          name="powerWords"
          kind="powerWord"
          items={formData?.powerWords}
          onChange={(value) =>
            handleChangeWithStore({ target: { name: "powerWords", value } })
          }
        />
      </Step>

      {/* step 13 */}
      <Step title="アイテム / 商品・商材">
        {/* Issue #91: タイトル20字 / URL / 内容40字 の3分割 × 3件 */}
        <StructuredItemsInput
          name="itemsProducts"
          kind="itemProduct"
          items={formData?.itemsProducts}
          onChange={(value) =>
            handleChangeWithStore({ target: { name: "itemsProducts", value } })
          }
        />
      </Step>

      {/* step 14 */}
      <Step title="実績・サービス登録">
        <PortfolioSection
          items={formData?.achievements || []}
          sectionName="実績"
          onChange={(achievements) =>
            handleChangeWithStore({ target: { name: "achievements", value: achievements } })
          }
        />
        <PortfolioSection
          items={formData?.services || []}
          sectionName="サービス"
          onChange={(services) =>
            handleChangeWithStore({ target: { name: "services", value: services } })
          }
        />
      </Step>

      {/* step 16 */}
      <Step title="ウェブサイト・SNSリンク">
        <LinkArrayInput
          links={formData?.links || []}
          onChange={(links) =>
            handleChangeWithStore({ target: { name: "links", value: links } })
          }
        />
        <SnsLinksInput
          snsLinks={formData?.snsLinks || []}
          onChange={(snsLinks) =>
            handleChangeWithStore({ target: { name: "snsLinks", value: snsLinks } })
          }
        />
      </Step>

      {/* step 17 */}
      <Step title="カスタマイズ">
        <UploadImage
          title="ヘッダー背景画像を設定してください"
          value={formData?.headerBackgroundImage}
          onChange={handleChangeWithStore}
          name="headerBackgroundImage"
          resetImage={() => resetField("headerBackgroundImage")}
          error={errors?.headerBackgroundImage}
        />
        <UploadImage
          title="プロフィール画像を設定してください"
          value={formData?.profileImage}
          onChange={handleChangeWithStore}
          name="profileImage"
          resetImage={() => resetField("profileImage")}
          error={errors?.profileImage}
        />
        <UploadImage
          title="リファーラルシートの背景画像をアップロードしてください"
          value={formData?.referralSheetBackgroundImage}
          onChange={handleChangeWithStore}
          name="referralSheetBackgroundImage"
          resetImage={() => resetField("referralSheetBackgroundImage")}
          error={errors?.referralSheetBackgroundImage}
        />
        <Fonts
          title="文字のタイプを選んでください"
          value={formData?.fontPreference}
          onChange={handleChangeWithStore}
          name="fontPreference"
        />
        <Themes
          title="色を選んでください"
          value={formData?.colorPreference}
          onChange={handleChangeWithStore}
          name="colorPreference"
        />
      </Step>
    </Stepper>
  );
};

export default BusinessSheetCreationForm;
